import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSurvey } from "../context/SurveyContext";
import { useAuth } from "../context/AuthContext";
import RankingQuestion from "./QuestionTypes/RankingQuestion";
import MatrixQuestion from "./QuestionTypes/MatrixQuestion";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Checkbox } from "./ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Progress } from "./ui/progress";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { CheckCircle2, Upload, X } from "lucide-react";
import "./SurveyResponse.css";
import { getBackendUrl } from "../config/env";

const SurveyResponse: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getSurvey, addResponse } = useSurvey();
  const { user, centerInfo, selectedCenterId } = useAuth();
  const [answers, setAnswers] = useState<{ [key: string]: any }>({});
  const [currentPage, setCurrentPage] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [survey, setSurvey] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [sessionId, setSessionId] = useState<string>("");
  const [startTime, setStartTime] = useState<number>(Date.now());
  const [externalData, setExternalData] = useState<any>(null);
  const visitRecorded = useRef(false);
  const questionsPerPage = 5;

  // URL에서 uuid 파라미터 디코딩
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const uuidParam = urlParams.get("uuid");

    if (uuidParam) {
      try {
        // Base64 디코딩 with UTF-8 support
        const decodedString = decodeURIComponent(escape(atob(uuidParam)));
        const decodedData = JSON.parse(decodedString);
        setExternalData(decodedData);
        console.log("External data decoded:", decodedData);
      } catch (error) {
        console.error("Failed to decode uuid parameter:", error);
        // 다른 방법 시도: TextDecoder 사용
        try {
          const binaryString = atob(uuidParam);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          const decodedString = new TextDecoder("utf-8").decode(bytes);
          const decodedData = JSON.parse(decodedString);
          setExternalData(decodedData);
          console.log("External data decoded (TextDecoder):", decodedData);
        } catch (error2) {
          console.error(
            "Failed to decode uuid parameter with TextDecoder:",
            error2
          );
        }
      }
    }
  }, []);

  // 조건부 로직 평가 함수 - 먼저 정의
  const evaluateConditionalLogic = (question: any) => {
    if (!question.conditionalLogic || question.conditionalLogic.length === 0) {
      return { show: true, skip: false };
    }

    // Check if any show_question logic exists
    const hasShowLogic = question.conditionalLogic.some(
      (logic: any) =>
        logic.action === "show" || logic.action === "show_question"
    );

    // Default behavior: if has show logic, hide by default
    let defaultShow = !hasShowLogic;
    let result = { show: defaultShow, skip: false };

    for (const logic of question.conditionalLogic) {
      const sourceAnswer = answers[logic.sourceQuestionId];
      let conditionMet = false;

      // Parse logic.value if it's a JSON string
      let logicValue = logic.value;
      if (
        typeof logicValue === "string" &&
        (logicValue.startsWith('"') || logicValue.startsWith("["))
      ) {
        try {
          logicValue = JSON.parse(logicValue);
        } catch (e) {
          // Keep original value if parsing fails
        }
      }

      switch (logic.condition) {
        case "equals":
          conditionMet = sourceAnswer === logicValue;
          break;
        case "not_equals":
          conditionMet = sourceAnswer !== logicValue;
          break;
        case "contains":
          conditionMet = Array.isArray(sourceAnswer)
            ? sourceAnswer.includes(logicValue)
            : String(sourceAnswer || "").includes(String(logicValue));
          break;
        case "not_contains":
          conditionMet = Array.isArray(sourceAnswer)
            ? !sourceAnswer.includes(logicValue)
            : !String(sourceAnswer || "").includes(String(logicValue));
          break;
        case "greater_than":
          conditionMet = Number(sourceAnswer) > Number(logicValue);
          break;
        case "less_than":
          conditionMet = Number(sourceAnswer) < Number(logicValue);
          break;
        case "is_answered":
          conditionMet =
            sourceAnswer !== undefined &&
            sourceAnswer !== "" &&
            sourceAnswer !== null;
          break;
        case "is_not_answered":
          conditionMet =
            sourceAnswer === undefined ||
            sourceAnswer === "" ||
            sourceAnswer === null;
          break;
        case "is_empty":
        case "is_not_empty":
          conditionMet =
            logic.condition === "is_empty"
              ? sourceAnswer === undefined ||
                sourceAnswer === "" ||
                sourceAnswer === null
              : sourceAnswer !== undefined &&
                sourceAnswer !== "" &&
                sourceAnswer !== null;
          break;
      }

      if (conditionMet) {
        switch (logic.action) {
          case "show":
          case "show_question":
            return { show: true, skip: false };
          case "hide":
          case "hide_question":
            return { show: false, skip: false };
          case "skip_to":
            return { show: true, skip: true, skipTo: logic.targetQuestionId };
          case "end_survey":
            return { show: false, skip: false, endSurvey: true };
        }
      }
    }

    return result;
  };

  // API에서 설문조사 데이터 로드
  useEffect(() => {
    loadSurvey();
    // 방문 기록
    recordVisit();
  }, [id]);

  // Generate or get session ID
  const getSessionId = () => {
    let sid = sessionStorage.getItem(`survey_session_${id}`);
    if (!sid) {
      sid = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem(`survey_session_${id}`, sid);
    }
    return sid;
  };

  // 방문 기록 함수
  const recordVisit = async () => {
    // useRef를 사용하여 한 번만 실행되도록 보장
    if (visitRecorded.current) {
      console.log("Visit recording already in progress or completed");
      return;
    }
    visitRecorded.current = true;

    try {
      const sid = getSessionId();
      setSessionId(sid);

      // 이미 이 세션에서 방문을 기록했는지 확인
      const visitRecordedTime = sessionStorage.getItem(
        `survey_visit_recorded_${id}_${sid}`
      );
      if (visitRecordedTime) {
        console.log("Visit already recorded for this session");
        setStartTime(parseInt(visitRecordedTime));
        return;
      }

      const response = await fetch(
        `${getBackendUrl()}/api/surveys/${id}/visit`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            sessionId: sid,
            timestamp: new Date().toISOString(),
            referrer: document.referrer,
            userAgent: navigator.userAgent,
          }),
        }
      );

      if (response.ok) {
        const startTime = Date.now();
        setStartTime(startTime);
        // 방문 기록 완료 표시
        sessionStorage.setItem(
          `survey_visit_recorded_${id}_${sid}`,
          startTime.toString()
        );
        console.log("Visit recorded successfully");
      }
    } catch (error) {
      console.error("Failed to record visit:", error);
    }
  };

  const loadSurvey = async () => {
    try {
      setLoading(true);
      // 먼저 API에서 시도
      const response = await fetch(
        `${getBackendUrl()}/api/surveys/public/${id}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setSurvey(data);
      } else {
        // API 실패 시 로컬 데이터 사용
        const localSurvey = getSurvey(id!);
        if (localSurvey) {
          setSurvey(localSurvey);
        } else {
          throw new Error("설문조사를 찾을 수 없습니다");
        }
      }
    } catch (error) {
      console.error("Error loading survey:", error);
      // 로컬 데이터로 폴백
      const localSurvey = getSurvey(id!);
      if (localSurvey) {
        setSurvey(localSurvey);
      } else {
        alert("설문조사를 불러올 수 없습니다");
        navigate("/");
      }
    } finally {
      setLoading(false);
    }
  };

  // 조건부 로직을 적용하여 표시할 질문들 필터링
  const getVisibleQuestions = () => {
    if (!survey) return [];

    const allQuestions = survey.questions || [];
    const visibleQuestions: any[] = [];

    for (const question of allQuestions) {
      const logicResult = evaluateConditionalLogic(question);
      if (logicResult.show) {
        visibleQuestions.push(question);
      }
    }

    return visibleQuestions;
  };

  const visibleQuestions = getVisibleQuestions();
  const totalPages = Math.ceil(visibleQuestions.length / questionsPerPage);
  const currentQuestions = visibleQuestions.slice(
    currentPage * questionsPerPage,
    (currentPage + 1) * questionsPerPage
  );

  // 조건부 로직에 따라 숨겨진 질문들의 답변 초기화
  useEffect(() => {
    if (!survey) return;

    const visibleQuestionIds = new Set(visibleQuestions.map((q) => q.id));

    setAnswers((prev) => {
      const newAnswers = { ...prev };
      let hasChanges = false;

      // 현재 답변이 있지만 더 이상 보이지 않는 질문들의 답변 삭제
      Object.keys(newAnswers).forEach((questionId) => {
        if (!visibleQuestionIds.has(questionId)) {
          delete newAnswers[questionId];
          hasChanges = true;
        }
      });

      return hasChanges ? newAnswers : prev;
    });
  }, [visibleQuestions, survey]);

  // 실제 답변 진행률 계산
  const calculateProgress = () => {
    if (visibleQuestions.length === 0) return 0;

    const answeredQuestions = visibleQuestions.filter((question) => {
      const answer = answers[question.id];
      if (answer === undefined || answer === null || answer === "")
        return false;

      // 체크박스의 경우 배열이 비어있으면 답변하지 않은 것
      if (Array.isArray(answer) && answer.length === 0) return false;

      return true;
    });

    return Math.round(
      (answeredQuestions.length / visibleQuestions.length) * 100
    );
  };

  const actualProgress = calculateProgress();

  const handleAnswerChange = (questionId: string, value: any) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: value,
    }));
  };

  const handleCheckboxChange = (
    questionId: string,
    option: string,
    checked: boolean
  ) => {
    const currentAnswers = answers[questionId] || [];
    if (checked) {
      setAnswers((prev) => ({
        ...prev,
        [questionId]: [...currentAnswers, option],
      }));
    } else {
      setAnswers((prev) => ({
        ...prev,
        [questionId]: currentAnswers.filter((a: string) => a !== option),
      }));
    }
  };

  const validateCurrentPage = () => {
    for (const question of currentQuestions) {
      if (question.required && !answers[question.id]) {
        alert(`"${question.question}" 항목은 필수입니다.`);
        return false;
      }
    }
    return true;
  };

  const handleNext = () => {
    if (validateCurrentPage()) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    setCurrentPage((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    if (!validateCurrentPage()) return;

    const allRequiredAnswered = survey?.questions
      .filter((q: any) => q.required)
      .every((q: any) => answers[q.id]);

    if (!allRequiredAnswered) {
      alert("모든 필수 항목을 작성해주세요.");
      return;
    }

    setSubmitting(true);
    try {
      // 파일 객체를 직렬화 가능한 형태로 변환
      const serializedAnswers = { ...answers };
      Object.keys(serializedAnswers).forEach((questionId) => {
        const question = survey?.questions.find(
          (q: any) => q.id === questionId
        );
        if (
          question?.type === "file-upload" &&
          serializedAnswers[questionId] instanceof File
        ) {
          const file = serializedAnswers[questionId] as File;
          serializedAnswers[questionId] = {
            fileName: file.name,
            fileSize: file.size,
            fileType: file.type,
            uploadedAt: new Date().toISOString(),
          };
        }
      });

      // 완료 시간 계산 (분 단위)
      const completionTime = Math.round((Date.now() - startTime) / 1000 / 60);

      // 응답 데이터 준비
      const centerUserId =
        selectedCenterId || user?.centerId || centerInfo?.centerId;

      // externalData가 있으면 사용자 정보 추출
      let respondentId = externalData?.userId || `anonymous_${Date.now()}`;
      let respondentName = externalData?.userName || undefined;
      let respondentEmail = externalData?.email || undefined;
      let respondentTel = externalData?.tel || undefined;

      const responseData = {
        surveyId: id!,
        respondentId: respondentId,
        respondentName: respondentName,
        respondentEmail: respondentEmail,
        respondentTel: respondentTel,
        externalData: externalData, // 전체 external 데이터 저장
        centerUserId: centerUserId,
        sessionId: sessionId,
        completionTime: completionTime,
        answers: Object.entries(serializedAnswers).map(
          ([questionId, value]) => ({
            questionId,
            value,
            answeredAt: new Date().toISOString(),
          })
        ),
        metadata: {
          userAgent: navigator.userAgent,
          deviceType: /Mobile|Android|iPhone/i.test(navigator.userAgent)
            ? "mobile"
            : "desktop",
          browser: getBrowserInfo(),
          os: getOSInfo(),
        },
        startedAt: new Date(startTime).toISOString(),
        completedAt: new Date().toISOString(),
        status: "completed",
      };

      // 백엔드 API로 전송
      const response = await fetch(`${getBackendUrl()}/api/responses`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(responseData),
      });

      if (!response.ok) {
        throw new Error("응답 저장에 실패했습니다");
      }

      const result = await response.json();
      console.log("Response saved:", result);

      // 방문 완료 상태 업데이트
      try {
        await fetch(
          `${getBackendUrl()}/api/surveys/${id}/visit/complete`,
          {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              sessionId: sessionId,
              responseId: result.id || result._id,
              completionTime: completionTime,
            }),
          }
        );
      } catch (error) {
        console.error("Failed to update visit completion:", error);
      }

      // 로컬 스토리지에도 저장 (백업)
      addResponse(id!, {
        surveyId: id!,
        answers: serializedAnswers,
      });

      setSubmitted(true);
    } catch (error) {
      console.error("Error submitting response:", error);
      alert("응답 제출에 실패했습니다. 다시 시도해주세요.");
    } finally {
      setSubmitting(false);
    }
  };

  // 브라우저 정보 추출
  const getBrowserInfo = () => {
    const userAgent = navigator.userAgent;
    if (userAgent.indexOf("Chrome") > -1) return "Chrome";
    if (userAgent.indexOf("Safari") > -1) return "Safari";
    if (userAgent.indexOf("Firefox") > -1) return "Firefox";
    if (userAgent.indexOf("Edge") > -1) return "Edge";
    return "Unknown";
  };

  // OS 정보 추출
  const getOSInfo = () => {
    const userAgent = navigator.userAgent;
    if (userAgent.indexOf("Windows") > -1) return "Windows";
    if (userAgent.indexOf("Mac") > -1) return "MacOS";
    if (userAgent.indexOf("Linux") > -1) return "Linux";
    if (userAgent.indexOf("Android") > -1) return "Android";
    if (userAgent.indexOf("iOS") > -1) return "iOS";
    return "Unknown";
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <Card className="w-96">
          <CardContent className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">설문조사를 불러오는 중...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <Card className="w-96">
          <CardContent className="p-8 text-center">
            <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              응답이 제출되었습니다!
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              설문조사에 참여해 주셔서 감사합니다.
            </p>
            {/* <Button
              onClick={() => navigate("/")}
              className="flex items-center gap-2"
            >
              <Home size={16} />
              홈으로 돌아가기
            </Button> */}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!survey) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <Card className="w-96">
          <CardContent className="p-8 text-center">
            <p className="text-gray-600 dark:text-gray-400">설문조사를 찾을 수 없습니다.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-3xl mx-auto px-4">
        <Card className="mb-6">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {survey.title}
            </CardTitle>
            {survey.description && (
              <p className="text-gray-600 dark:text-gray-400 mt-2">{survey.description}</p>
            )}
            <div className="mt-4">
              <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
                <span>진행률: {actualProgress}%</span>
                <span>
                  {currentPage + 1} / {totalPages} 페이지
                </span>
              </div>
              <Progress value={actualProgress} className="h-2" />
            </div>
          </CardHeader>
        </Card>

        <div className="space-y-6">
          {currentQuestions.map((question, index) => (
            <Card key={question.id}>
              <CardContent className="p-6">
                <div className="mb-4 flex justify-between items-start">
                  <Label className="text-lg font-medium text-gray-900 flex-1 pr-2">
                    {currentPage * questionsPerPage + index + 1}.{" "}
                    {question.question}
                  </Label>
                  {question.required && (
                    <Badge variant="destructive" className="flex-shrink-0">
                      필수
                    </Badge>
                  )}
                </div>

                {question.type === "multiple-choice" && (
                  <RadioGroup
                    value={answers[question.id] || ""}
                    onValueChange={(value) =>
                      handleAnswerChange(question.id, value)
                    }
                    className="space-y-3"
                  >
                    {question.options?.map((option: string) => (
                      <div key={option} className="flex items-center space-x-2">
                        <RadioGroupItem
                          value={option}
                          id={`${question.id}-${option}`}
                        />
                        <Label
                          htmlFor={`${question.id}-${option}`}
                          className="cursor-pointer"
                        >
                          {option}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                )}

                {question.type === "checkbox" && (
                  <div className="space-y-3">
                    {question.options?.map((option: string) => (
                      <div key={option} className="flex items-center space-x-2">
                        <Checkbox
                          id={`${question.id}-${option}`}
                          checked={(answers[question.id] || []).includes(
                            option
                          )}
                          onCheckedChange={(checked) =>
                            handleCheckboxChange(question.id, option, !!checked)
                          }
                        />
                        <Label
                          htmlFor={`${question.id}-${option}`}
                          className="cursor-pointer"
                        >
                          {option}
                        </Label>
                      </div>
                    ))}
                  </div>
                )}

                {question.type === "text" && (
                  <Textarea
                    value={answers[question.id] || ""}
                    onChange={(e) =>
                      handleAnswerChange(question.id, e.target.value)
                    }
                    placeholder="답변을 입력하세요"
                    rows={4}
                    className="w-full"
                  />
                )}

                {question.type === "rating" && (
                  <RadioGroup
                    value={answers[question.id]?.toString() || ""}
                    onValueChange={(value) =>
                      handleAnswerChange(question.id, parseInt(value))
                    }
                    className="flex flex-wrap gap-4"
                  >
                    {Array.from(
                      {
                        length:
                          (question.maxRating || 5) -
                          (question.minRating || 1) +
                          1,
                      },
                      (_, i) => (question.minRating || 1) + i
                    ).map((rating) => (
                      <div key={rating} className="flex items-center space-x-2">
                        <RadioGroupItem
                          value={rating.toString()}
                          id={`${question.id}-${rating}`}
                        />
                        <Label
                          htmlFor={`${question.id}-${rating}`}
                          className="cursor-pointer font-medium"
                        >
                          {rating}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                )}

                {question.type === "dropdown" && (
                  <Select
                    value={answers[question.id] || ""}
                    onValueChange={(value) =>
                      handleAnswerChange(question.id, value)
                    }
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="선택하세요" />
                    </SelectTrigger>
                    <SelectContent>
                      {question.options?.map((option: string) => (
                        <SelectItem key={option} value={option}>
                          {option}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}

                {question.type === "matrix" && (
                  <MatrixQuestion
                    question={question}
                    value={answers[question.id] || {}}
                    onChange={(value) => handleAnswerChange(question.id, value)}
                  />
                )}

                {question.type === "ranking" && (
                  <RankingQuestion
                    question={question}
                    value={answers[question.id] || []}
                    onChange={(value) => handleAnswerChange(question.id, value)}
                  />
                )}

                {question.type === "file-upload" && (
                  <div className="space-y-3">
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                      <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                      <Input
                        type="file"
                        accept={question.allowedFileTypes?.join(",") || "*"}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            if (
                              question.maxFileSize &&
                              file.size > question.maxFileSize * 1024 * 1024
                            ) {
                              alert(
                                `파일 크기가 ${question.maxFileSize}MB를 초과합니다.`
                              );
                              e.target.value = "";
                              return;
                            }
                            handleAnswerChange(question.id, file);
                          } else {
                            handleAnswerChange(question.id, null);
                          }
                        }}
                        className="cursor-pointer"
                      />
                    </div>

                    <div className="text-sm text-gray-500 space-y-1">
                      {question.allowedFileTypes &&
                        question.allowedFileTypes.length > 0 && (
                          <p>
                            허용 형식: {question.allowedFileTypes.join(", ")}
                          </p>
                        )}
                      {question.maxFileSize && (
                        <p>최대 크기: {question.maxFileSize}MB</p>
                      )}
                    </div>

                    {answers[question.id] && (
                      <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                        <span className="text-sm text-green-800">
                          선택된 파일: {answers[question.id].name}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            handleAnswerChange(question.id, null);
                            const fileInput = (e.target as HTMLElement)
                              .closest(".space-y-3")
                              ?.querySelector(
                                'input[type="file"]'
                              ) as HTMLInputElement;
                            if (fileInput) fileInput.value = "";
                          }}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <X size={16} />
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        <Card className="mt-6">
          <CardContent className="p-6">
            <div className="flex justify-between">
              {currentPage > 0 ? (
                <Button variant="outline" onClick={handlePrevious}>
                  이전
                </Button>
              ) : (
                <div></div>
              )}

              {currentPage < totalPages - 1 ? (
                <Button onClick={handleNext}>다음</Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {submitting ? "제출 중..." : "제출"}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SurveyResponse;
